/**
 * ================================================================================================
 *  DRAGGING A HOME TILE WITH A POINTER — AND WHY IT IS NO LONGER HTML5 DRAG-AND-DROP.
 * ================================================================================================
 *
 * The Owner, 2026-11-01, for the third round running: *"גרירה אמיתית בעכבר — עדיין אינה עובדת
 * אצלי."*
 *
 * ── WHAT WAS MEASURED, IN A REAL BROWSER, BEFORE ANYTHING WAS CHANGED ───────────────────────────
 *
 * The home screen was rendered in Chrome through `tools/ui-harness` at 1440x900 and the cash-flow
 * tile's handle was dragged onto the tile below it with the real mouse input the pane drives. The
 * order did not change. Every event of the gesture was recorded at the document, in order:
 *
 *     pointerdown  mousedown  pointermove  mousemove  dragstart  drag
 *     dragenter    dragover   drag         dragenter  drag       dragend
 *
 * **There is no `drop`, and `dragover` arrived exactly once with `defaultPrevented === false`.**
 * The one `dragover` of the whole gesture was over the SOURCE tile, which the guard correctly
 * declines — and no further `dragover` was ever dispatched to the tile the pointer was actually
 * over, only `dragenter`. A drop the browser was never asked to allow is a drop the browser
 * refuses, which is precisely what the person sees: the card snaps back and nothing happens.
 *
 * ── AND THE DEEPER REASON, WHICH IS WHY THIS IS A REPLACEMENT AND NOT ANOTHER PATCH ─────────────
 *
 * This is the second fix to the same gesture. The first one (2026-10-29) was right about its own
 * cause — a `dragover` guard reading React state that the first event of a drag arrives too early
 * to see — and the gesture still did not work. That is the signal this department records as
 * *three occurrences of one signature means the diagnosis is wrong, not the patch*; it is here at
 * two, and the honest reading is that the MECHANISM cannot be made to work reliably:
 *
 *   1. **Nothing we own can exercise it.** jsdom implements no drag-and-drop at all, and the
 *      browser pane's synthetic input produces the truncated event stream above. So every test
 *      that said the drag worked was testing the keyboard path beside it. A gesture that cannot
 *      be driven cannot be proved, and an unprovable control is one that breaks quietly.
 *   2. **It does not exist on a touch screen.** HTML5 drag-and-drop has no touch implementation;
 *      the Owner asks for a home screen that behaves *"בין מכשירים"*.
 *   3. **The drop target is decided by an accumulation of `dragover` events**, and the browser —
 *      not this code — decides how many of those to dispatch and where.
 *
 * Pointer events have none of those properties. `pointerdown`/`pointermove`/`pointerup` fire for a
 * mouse, a finger and a stylus alike; they are ordinary input the browser pane drives faithfully;
 * and **the drop is decided at `pointerup` from the pointer's position**, so a gesture that
 * produced one move event lands exactly where a gesture that produced forty does.
 *
 * ── THE TARGET IS FOUND BY HIT-TESTING, NOT BY LISTENERS ON EVERY TILE ──────────────────────────
 *
 * `setPointerCapture` sends every subsequent event of the gesture to the handle, which is what
 * makes the drag survive the pointer leaving the tile. It also means no other tile will receive an
 * event, so the tile under the pointer is found with `document.elementFromPoint` — capture
 * redirects EVENTS and does not change hit-testing. One pair of handlers for the whole screen
 * instead of a pair on every tile, and no state in eleven places to get out of step.
 *
 * **A DROP ACROSS REGIONS DOES NOTHING**, enforced here by comparing `data-tile-region` rather
 * than in each caller: the two columns differ in width by design and `homeOrder.ts` holds one
 * order per region.
 *
 * ── WHAT THIS FILE DOES NOT CLAIM ───────────────────────────────────────────────────────────────
 *
 * `document.elementFromPoint` needs layout, and jsdom has none — it answers `null` for every
 * point. So a `.dom.test.tsx` of this hook would prove that nothing happens, which is true in
 * jsdom and says nothing about Chrome. **The evidence for this file is a browser**, recorded in
 * the round's delivery note, and the keyboard path beside it keeps the suite it always had.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { HomeRegion } from './homeOrder';

/** The gesture in flight. A ref and not state: `pointermove` must read it in its own frame. */
interface Gesture {
  readonly id: string;
  readonly region: HomeRegion;
  readonly pointerId: number;
  /** The tile the pointer is over, or `null`. Mutated in place, for the reason above. */
  target: string | null;
}

export interface TileDrag {
  /** The tile being carried, for the fade. A render-time fact; nothing decides on it. */
  readonly carrying: string | null;
  /** The tile the carried one would take the place of, for the drop indicator. */
  readonly over: string | null;
  readonly onPointerDown: (
    event: ReactPointerEvent<HTMLElement>,
    id: string,
    region: HomeRegion,
  ) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: () => void;
}

/**
 * THE TILE UNDER A POINT, or `null` for *not a tile this gesture may drop on*.
 *
 * Three separate refusals and each is its own sentence: nothing there, the tile being carried, and
 * a tile in the other region. Attributes rather than a React tree walk, because the element comes
 * back from the DOM and not from a render.
 */
function tileUnder(x: number, y: number, held: Gesture): string | null {
  if (typeof document === 'undefined') return null;
  const at = document.elementFromPoint(x, y);
  const tile = at === null ? null : at.closest('[data-tile-id]');
  if (tile === null) return null;
  const id = tile.getAttribute('data-tile-id') ?? '';
  if (id === '' || id === held.id) return null;
  if (tile.getAttribute('data-tile-region') !== held.region) return null;
  return id;
}

/**
 * ONE DRAG CONTROLLER FOR THE WHOLE SCREEN.
 *
 * `drop` is called ONCE, at `pointerup`, with the region, the carried tile and the tile it was
 * dropped on — never during the gesture. An order written on every `pointermove` would be a
 * preference push per pixel.
 */
export function useTileDrag(
  drop: (region: HomeRegion, dragged: string, target: string) => void,
): TileDrag {
  const gesture = useRef<Gesture | null>(null);
  const [carrying, setCarrying] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const finish = useCallback(
    (apply: boolean): void => {
      const held = gesture.current;
      gesture.current = null;
      setCarrying(null);
      setOver(null);
      if (held !== null && apply && held.target !== null) {
        drop(held.region, held.id, held.target);
      }
    },
    [drop],
  );

  /*
   * ESCAPE ABANDONS THE DRAG, which a pointer gesture has to provide for itself — the browser's
   * own drag-and-drop did it and nothing does it now. Bound only while a tile is carried, so the
   * home screen adds no key listener to a page nobody is dragging on.
   */
  useEffect(() => {
    if (carrying === null) return undefined;
    const abandon = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', abandon);
    return () => {
      window.removeEventListener('keydown', abandon);
    };
  }, [carrying, finish]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>, id: string, region: HomeRegion): void => {
      // A right-click or a middle-click is not a drag. Touch and pen report button 0 as well.
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      gesture.current = { id, region, pointerId: event.pointerId, target: null };
      setCarrying(id);
      setOver(null);
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /*
         * jsdom does not implement pointer capture, and a browser refuses it for a pointer that is
         * no longer down. Neither is a reason to lose the gesture: without capture the drag still
         * works while the pointer stays inside the handle's own element, and the keyboard path is
         * untouched.
         */
      }
      // Stops the text selection a press-and-move otherwise starts across the card.
      event.preventDefault();
    },
    [],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    const held = gesture.current;
    if (held === null || event.pointerId !== held.pointerId) return;
    const target = tileUnder(event.clientX, event.clientY, held);
    if (target === held.target) return;
    held.target = target;
    setOver(target);
  }, []);

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const held = gesture.current;
      if (held === null || event.pointerId !== held.pointerId) return;
      /*
       * THE POSITION AT RELEASE DECIDES, and this is the line that makes the gesture reliable: a
       * drag that produced a single move event still drops where the pointer was let go. The last
       * hovered tile is the fallback only when the release landed on nothing — that is the tile
       * the indicator was drawn on, so it is what the person was looking at.
       */
      held.target = tileUnder(event.clientX, event.clientY, held) ?? held.target;
      finish(true);
    },
    [finish],
  );

  const onPointerCancel = useCallback((): void => {
    finish(false);
  }, [finish]);

  return { carrying, over, onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
